import { useCallback, useMemo, useState, type AnimationEvent, type KeyboardEvent } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { fadeOut } from 'components/utils/animations'
import zIndex from 'styles/tools/z-index'
import type { TextDirection } from 'lib/dom/logicalScroll'
import { COMPOSER_STACK_GAP } from './composerLayout'
import { useMicrophoneCentreOffset } from './hooks/useMicrophoneCentreOffset'

const TAIL_SIZE = 6

const messages = defineMessages({
  accept: {
    id: 'DD8fftfrVWJUkOVvWKC0',
    defaultMessage: 'Try planning by voice',
    description: 'Button above the TripPlanner chat microphone, shown while the composer is empty, that starts recording a voice message',
  },
  dismiss: {
    id: 'U7IxeRzuApY95xxL1e4t',
    defaultMessage: 'Dismiss voice suggestion',
    description: 'Accessibility label for the close button on the TripPlanner chat nudge suggesting a voice message',
  },
})

const slideIn = keyframes`
  from { opacity: 0; translate: 0 ${rem(6)}; }
  to { opacity: 1; translate: none; }
`

// Only the pill takes pointer events, so the slot's empty width never blocks what it overlays.
const Slot = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  pointer-events: none;

  &.overlay {
    position: absolute;
    inset-block-end: calc(100% + ${rem(COMPOSER_STACK_GAP)});
    inset-inline: 0;
    z-index: ${zIndex.min};
  }
`

const Pill = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.palette.highlight.primary.darkBackground};
  border-radius: ${props => props.theme.borderRadius.round};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
  pointer-events: auto;
  animation: ${slideIn} 0.35s ease-out;

  &.closing {
    animation: ${fadeOut} 0.2s ease-in forwards;
  }
`

const Tail = styled.span`
  position: absolute;
  inset-block-start: 100%;
  border-inline: ${rem(TAIL_SIZE)} solid transparent;
  border-block-start: ${rem(TAIL_SIZE)} solid ${props => props.theme.palette.highlight.primary.darkBackground};
`

interface Props {
  anchorElement: HTMLElement | null
  textDirection: TextDirection
  /** Floats over what sits above the composer instead of taking space in the column */
  overlay: boolean
  isClosing: boolean
  onAccept: () => void
  onDismiss: () => void
  onClosed: () => void
}

function VoiceChatNudge({ anchorElement, textDirection, overlay, isClosing, onAccept, onDismiss, onClosed }: Props) {
  const intl = useIntl()
  const [pillElement, setPillElement] = useState<HTMLDivElement | null>(null)
  const microphoneCentre = useMicrophoneCentreOffset(pillElement, anchorElement, textDirection)

  // Once overlaid, stay overlaid: dropping into the column while open (e.g. the context banner clearing) jumps the conversation.
  const [isOverlaid, setIsOverlaid] = useState(overlay)
  if (overlay && !isOverlaid) setIsOverlaid(true)

  const tailStyle = useMemo(() => ({
    insetInlineEnd: `max(0px, calc(${microphoneCentre}px - ${rem(TAIL_SIZE)}))`,
  }), [microphoneCentre])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') onDismiss()
  }, [onDismiss])

  const handleAnimationEnd = useCallback((event: AnimationEvent<HTMLDivElement>) => {
    if (isClosing && event.target === event.currentTarget) onClosed()
  }, [isClosing, onClosed])

  return (
    <Slot className={clsx({ overlay: isOverlaid })}>
      <Pill
        ref={setPillElement}
        className={clsx({ closing: isClosing })}
        onKeyDown={handleKeyDown}
        onAnimationEnd={handleAnimationEnd}
      >
        <TextButton
          kind="primary"
          size="small"
          shape="round"
          onClick={onAccept}
        >
          {intl.formatMessage(messages.accept)}
        </TextButton>
        <IconButton
          kind="primary"
          size="small"
          shape="circle"
          onClick={onDismiss}
          aria-label={intl.formatMessage(messages.dismiss)}
        >
          <LineTimesIcon />
        </IconButton>
        <Tail style={tailStyle} />
      </Pill>
    </Slot>
  )
}

export default VoiceChatNudge
